import type { AnalysisResult } from '../AnalysisResult';
import type { AnalysisConfiguration, TeoText } from '../TeoText';

export interface HistoryData {
  configuration: AnalysisConfiguration;
  text: string;
  serialized: string | undefined;
  lastEdited: number;
  analysis: ReturnType<AnalysisResult['toJSON']>;
}

export class HistoryEntry {
  #data: HistoryData;
  #text: TeoText;

  static create(text: TeoText) {
    if (!text.analysis) throw new Error('Missing analysis for history entry');
    if (!text.configIsSet()) throw new Error('Missing text configuration for history entry');

    const analysis = text.analysis?.toJSON();
    if (!analysis) throw new Error('Missing analysis for history entry');

    return new HistoryEntry(text, {
      configuration: text.configuration as AnalysisConfiguration,
      text: text.text,
      serialized: text.serialized,
      lastEdited: text.lastEdited,
      analysis,
    });
  }

  /**
   * Creates a new history entry.
   * @param text The text to create the entry for.
   * @param virtual If true, the entry is not part of the history and is only used to restore the text.
   */
  protected constructor(text: TeoText, data: HistoryData) {
    this.#text = text;
    this.#data = data;
  }

  get value() {
    return this.#data.analysis.data.tpi;
  }

  get date() {
    return this.#data.lastEdited;
  }

  restore() {
    this.#text.history.restore(this.#data);
  }

  select() {
    this.#text.history.select(this.#data);
  }

  setTextDiff(show = true) {
    this.#text.diff.update(this.#data.text, show);
  }

  toJSON(): HistoryData {
    return this.#data;
  }

  static fromJSON(text: TeoText, json: HistoryData) {
    const entry = new HistoryEntry(text, json);
    entry.#data = json;

    return entry;
  }
}
